/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaItpExecutions.ts
 * FILE_ID: JOBHUB-QA-ITP-EXECUTIONS-001
 * PURPOSE: Load every area the ITP resolver named, via the shared query key.
 * LANDMINE: Do not cache-peek. An uncached area must fetch, not vanish.
 *   Wait for `ready` (the bulk read has filled the cache, or fell back) so
 *   each area is not also fetched on its own while that read is in flight.
 */
import { useQueryClient, useQueries } from '@tanstack/react-query'
import type { QaApi, QaAreaExecution } from '../../qa/qaApi'
import { QA_AREA_EXEC_FRESH_MS } from './useQaAreaExecutionsWarm'
import { currentWriteGeneration, keepCachedArea } from './qaExecutionCache'

export function useQaItpExecutions(
  areaIds: readonly string[],
  api: Pick<QaApi, 'getAreaExecution'>,
  ready = true,
) {
  const queryClient = useQueryClient()
  const queries = useQueries({
    queries: areaIds.map((id) => ({
      queryKey: ['qa-area-exec', id] as const,
      queryFn: async () => {
        const started = currentWriteGeneration(id)
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (keepCachedArea)
        const fetched = await api.getAreaExecution(id, { photos: false })
        return keepCachedArea(id, started, fetched, queryClient.getQueryData(['qa-area-exec', id]))
      },
      enabled: ready && Boolean(id) && typeof api.getAreaExecution === 'function',
      staleTime: QA_AREA_EXEC_FRESH_MS,
      refetchOnWindowFocus: false,
    })),
  })
  return {
    executions: queries
      .map((row) => row.data)
      .filter((row): row is QaAreaExecution => Boolean(row)),
    pending: areaIds.length > 0 && queries.some((row) => row.isPending || row.isLoading),
  }
}
